import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { API } from '../api';
import EntityHeader, { EntityHeaderSkeleton } from '../ui/EntityHeader';
import EntityPage, { EntityLoadProblem, EntityTabs, TabTable, clearFiltersAction, useEntityTab } from '../ui/EntityPage';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import Badge, { SeverityBadge, StatusBadge, highestSeverity } from '../ui/Badge';
import { formatCount, formatDate, plural } from '../ui/format';
import useAsync from '../ui/useAsync';
import usePagedQuery from '../ui/usePagedQuery';
import useUrlFilters from '../ui/useUrlFilters';
import EntityTickets, { fetchEntityTickets } from './EntityTickets';
import FindingsTab, { fetchFindingsCount } from './FindingsTab';
import CreateTicketsPanel from './CreateTicketsPanel';
import ExportButton from './ExportPanel';

const TAB_IDS = ['findings', 'vulnerabilities', 'hosts', 'versions', 'source', 'tickets'];
const muted = (text) => <span className="text-graphite">{text}</span>;
const dateCell = (value) => <span className="text-graphite tabular-nums">{formatDate(value) || '—'}</span>;
const countCell = (n) => (n ? formatCount(n) : muted('0'));

// Totals for every tab, plus the highest severity. The API sorts findings by
// severity, so the most severe one is either the first or the last row.
async function loadOverview(pkg) {
  const settle = (promise) => promise.then((r) => r, () => null);
  const [vulns, hosts, findings, versions, source] = await Promise.all([
    settle(API.GET_VULNS_FOR_PACKAGE({ packageId: pkg.id, limit: 1 })),
    settle(API.GET_HOSTS_FOR_PACKAGE({ packageId: pkg.id, limit: 1 })),
    settle(fetchFindingsCount({ packageId: pkg.id })),
    settle(API.GET_VERSIONS_FOR_PACKAGE({ packageName: pkg.name, limit: 1 })),
    pkg.source ? settle(API.GET_PACKAGES({ kw: pkg.source, limit: 1 })) : null,
  ]);
  let last = null;
  if (vulns && vulns.total > 1) last = await settle(API.GET_VULNS_FOR_PACKAGE({ packageId: pkg.id, offset: vulns.total - 1, limit: 1 }));
  const ends = [...(vulns?.data || []), ...(last?.data || [])];
  return {
    vulns: vulns?.total,
    hosts: hosts?.total,
    findings: findings ?? undefined,
    versions: versions?.total,
    source: source?.total,
    severity: vulns ? highestSeverity(ends.map((r) => r.severity)) : undefined,
  };
}

const VULN_COLUMNS = [
  { key: 'cve', header: 'CVE', render: (r) => <EntityLink kind="cve" cve={r.cve} /> },
  { key: 'severity', header: 'Severity', render: (r) => <SeverityBadge severity={r.severity} /> },
  {
    key: 'description',
    header: 'Description',
    grow: true,
    minWidth: 220,
    fullText: (r) => r.vuln_description,
    render: (r) => r.vuln_description || muted('No description'),
  },
  { key: 'category', header: 'Category', maxWidth: 200, fullText: (r) => r.category, render: (r) => r.category || muted('—') },
  { key: 'fixed', header: 'Fixed in', render: (r) => (r.fixed_version ? <span className="font-mono text-id text-ok">{r.fixed_version}</span> : muted('No fix yet')) },
  { key: 'hosts', header: 'Hosts', align: 'right', render: (r) => countCell(r.affected_host_count) },
  { key: 'published', header: 'Published', render: (r) => dateCell(r.vuln_published_at) },
];

const HOST_COLUMNS = [
  { key: 'host', header: 'Host', render: (h) => <EntityLink kind="host" id={h.host_id} name={h.hostname} /> },
  { key: 'codename', header: 'OS codename', render: (h) => h.host_codename || muted('—') },
  { key: 'region', header: 'Region', render: (h) => h.region || muted('Not set') },
  { key: 'team', header: 'Team', render: (h) => h.team_flow || muted('Not set') },
  { key: 'status', header: 'Status', render: (h) => <StatusBadge status={h.status} /> },
  { key: 'first', header: 'First seen', render: (h) => dateCell(h.first_seen_at) },
  { key: 'last', header: 'Last seen', render: (h) => dateCell(h.last_seen_at) },
];

const versionColumns = (currentId) => [
  {
    key: 'version',
    header: 'Version',
    render: (p) => (
      <span className="flex items-center gap-2 min-w-0">
        {p.id === currentId ? <span className="font-mono text-id text-ink">{p.version}</span> : <EntityLink kind="package" id={p.id} name={p.version} />}
        {p.id === currentId && <Badge tone="signal">This version</Badge>}
      </span>
    ),
  },
  { key: 'arch', header: 'Architecture', render: (p) => p.package_arch || muted('—') },
  { key: 'vendor', header: 'Vendor', grow: true, render: (p) => p.vendor || muted('—') },
];

const SOURCE_COLUMNS = [
  { key: 'package', header: 'Package', render: (p) => <EntityLink kind="package" id={p.id} name={p.name} /> },
  { key: 'version', header: 'Version', mono: true, render: (p) => p.version },
  { key: 'arch', header: 'Architecture', render: (p) => p.package_arch || muted('—') },
  { key: 'description', header: 'Description', grow: true, fullText: (p) => p.description, render: (p) => p.description || muted('No description') },
  { key: 'hosts', header: 'Hosts', align: 'right', render: (p) => countCell(p.host_count) },
  { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (p) => countCell(p.vuln_count) },
];

const VULN_FILTER_DEFAULTS = { cve: '' };
const VULN_FILTERS = [{ key: 'cve', type: 'search', label: 'CVE', placeholder: 'CVE-2024-…', mono: true, width: 240 }];
const HOST_FILTER_DEFAULTS = { host: '' };
const HOST_FILTERS = [{ key: 'host', type: 'search', label: 'Host', placeholder: 'Hostname', mono: true, width: 240 }];

function VulnerabilitiesTab({ pkg }) {
  const [filters, setFilters] = useUrlFilters(VULN_FILTER_DEFAULTS);
  const key = `${pkg.id}|${filters.cve}`;
  const params = { packageId: pkg.id, kw: filters.cve };
  const fetchPage = useCallback((paging) => API.GET_VULNS_FOR_PACKAGE({ ...params, ...paging }), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = usePagedQuery(fetchPage, key);
  return (
    <TabTable
      actions={<ExportButton query={{ endpoint: 'vulns_for_package', params }} />}
      label={`Vulnerabilities in ${pkg.name} ${pkg.version}`}
      filters={VULN_FILTERS}
      values={filters}
      onFiltersChange={setFilters}
      query={query}
      filtered={Boolean(filters.cve)}
      columns={VULN_COLUMNS}
      rowKey={(r) => r.vulnerability_id}
      errorTitle="Vulnerabilities could not be loaded"
      empty={<EmptyState title="No known vulnerabilities">No active host reports a CVE for this package version.</EmptyState>}
      filteredEmpty={(
        <EmptyState title="No vulnerabilities match this CVE" action={clearFiltersAction(() => setFilters(VULN_FILTER_DEFAULTS))}>
          The CVE filter matches part of the ID, so “2024-6” finds every CVE containing it.
        </EmptyState>
      )}
    />
  );
}

function HostsTab({ pkg }) {
  const [filters, setFilters] = useUrlFilters(HOST_FILTER_DEFAULTS);
  const key = `${pkg.id}|${filters.host}`;
  const params = { packageId: pkg.id, kw: filters.host };
  const fetchPage = useCallback((paging) => API.GET_HOSTS_FOR_PACKAGE({ ...params, ...paging }), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const query = usePagedQuery(fetchPage, key);
  return (
    <TabTable
      actions={<ExportButton query={{ endpoint: 'hosts_for_package', params }} />}
      label={`Hosts with ${pkg.name} ${pkg.version}`}
      filters={HOST_FILTERS}
      values={filters}
      onFiltersChange={setFilters}
      query={query}
      filtered={Boolean(filters.host)}
      columns={HOST_COLUMNS}
      rowKey={(h) => h.host_package_id}
      errorTitle="Hosts could not be loaded"
      empty={<EmptyState title="Not installed on any host">No host currently reports this package version.</EmptyState>}
      filteredEmpty={(
        <EmptyState title="No hosts match this name" action={clearFiltersAction(() => setFilters(HOST_FILTER_DEFAULTS))}>
          The host filter matches part of the hostname.
        </EmptyState>
      )}
    />
  );
}

function VersionsTab({ pkg }) {
  const fetchPage = useCallback((paging) => API.GET_VERSIONS_FOR_PACKAGE({ packageName: pkg.name, ...paging }), [pkg.name]);
  const query = usePagedQuery(fetchPage, pkg.name);
  const columns = useMemo(() => versionColumns(pkg.id), [pkg.id]);
  return (
    <TabTable
      actions={<ExportButton query={{ endpoint: 'versions_for_package', params: { packageName: pkg.name } }} />}
      label={`Versions of ${pkg.name}`}
      query={query}
      columns={columns}
      rowKey={(p) => p.id}
      errorTitle="Versions could not be loaded"
      empty={<EmptyState title="No other versions">This is the only version of {pkg.name} in the inventory.</EmptyState>}
    />
  );
}

function SourceTab({ pkg }) {
  const fetchPage = useCallback((paging) => API.GET_PACKAGES({ kw: pkg.source, ...paging }), [pkg.source]);
  const query = usePagedQuery(fetchPage, pkg.source || '', { enabled: Boolean(pkg.source) });
  return (
    <TabTable
      actions={pkg.source && <ExportButton query={{ endpoint: 'packages', params: { kw: pkg.source } }} />}
      label={`Packages built from ${pkg.source}`}
      query={pkg.source ? query : { ...query, loading: false }}
      columns={SOURCE_COLUMNS}
      rowKey={(p) => p.id}
      errorTitle="Packages could not be loaded"
      empty={
        pkg.source
          ? <EmptyState title="No packages from this source">No package name contains the source name {pkg.source}.</EmptyState>
          : <EmptyState title="No source package recorded">The scanner didn’t report which source package this was built from.</EmptyState>
      }
    />
  );
}

export default function PackagePage() {
  const { id } = useParams();
  const [tab, setTab] = useEntityTab(TAB_IDS);
  const entity = useAsync(() => API.GET_PACKAGE(id), id);
  const pkg = entity.data;
  const overview = useAsync(() => loadOverview(pkg), pkg ? pkg.id : null, { enabled: Boolean(pkg) });
  const counts = overview.data || {};
  const [panelOpen, setPanelOpen] = useState(false);
  const [ticketsVersion, setTicketsVersion] = useState(0);
  const tickets = useAsync(() => fetchEntityTickets({ package: pkg.name }, { limit: 1 }), pkg ? `${pkg.name}|${ticketsVersion}` : null, { enabled: Boolean(pkg) });

  const crumbs = pkg
    ? [{ label: 'Packages', to: '/packages' }, { label: pkg.name, to: `/packages?kw=${encodeURIComponent(pkg.name)}` }, { label: pkg.version }]
    : [{ label: 'Packages', to: '/packages' }, { label: 'Package' }];

  if (entity.error) {
    return <EntityLoadProblem crumbs={crumbs} noun="package" identifier={id} error={entity.error} onRetry={entity.retry} listPath="/packages" listLabel="Go to Packages" />;
  }

  const sourceLink = pkg?.source && (
    <button type="button" onClick={() => setTab('source')} className="font-mono text-id font-semibold text-signal hover:underline">
      {pkg.source}
    </button>
  );

  const loadingValue = overview.loading ? muted('…') : null;
  const facts = pkg && [
    {
      label: 'Highest severity',
      value: overview.loading ? loadingValue : (counts.severity ? <SeverityBadge severity={counts.severity} /> : <span className="font-normal text-graphite">None known</span>),
    },
    { label: 'Vulnerabilities', value: counts.vulns !== undefined ? formatCount(counts.vulns) : loadingValue },
    { label: 'Installed on', value: counts.hosts !== undefined ? plural(counts.hosts, 'host') : loadingValue },
    { label: 'Format', value: pkg.format },
    { label: 'Architecture', value: pkg.package_arch },
    { label: 'Vendor', value: pkg.vendor },
    { label: 'Section', value: pkg.section },
    { label: 'Source package', value: sourceLink },
  ];

  return (
    <EntityPage>
      {!pkg ? (
        <EntityHeaderSkeleton crumbs={crumbs} />
      ) : (
        <EntityHeader
          crumbs={crumbs}
          identifier={pkg.name}
          secondary={pkg.version}
          severity={counts.severity}
          summary={pkg.description}
          facts={facts}
          actions={(
            <Button onClick={() => setPanelOpen(true)}>
              <Plus size={15} strokeWidth={2} className="text-graphite" aria-hidden="true" />
              Create tickets
            </Button>
          )}
        />
      )}
      {pkg && (
        <CreateTicketsPanel
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          lock={{ type: 'package-based', packageName: pkg.name }}
          onCreated={({ created }) => {
            setTicketsVersion((n) => n + 1);
            if (created > 0) setTab('tickets');
          }}
        />
      )}
      {pkg && (
        <EntityTabs
          label={`${pkg.name} ${pkg.version} details`}
          value={tab}
          onChange={setTab}
          tabs={[
            {
              id: 'findings',
              label: 'Findings',
              count: counts.findings,
              render: () => (
                <FindingsTab
                  scope={{ packageId: pkg.id }}
                  hide={['package', 'installed']}
                  label={`Findings for ${pkg.name} ${pkg.version}`}
                />
              ),
            },
            { id: 'vulnerabilities', label: 'Vulnerabilities', count: counts.vulns, render: () => <VulnerabilitiesTab pkg={pkg} /> },
            { id: 'hosts', label: 'Hosts', count: counts.hosts, render: () => <HostsTab pkg={pkg} /> },
            { id: 'versions', label: 'Other versions', count: counts.versions, render: () => <VersionsTab pkg={pkg} /> },
            { id: 'source', label: 'Same source', count: counts.source, render: () => <SourceTab pkg={pkg} /> },
            {
              id: 'tickets',
              label: 'Tickets',
              count: tickets.data?.total,
              render: () => (
                <EntityTickets
                  filter={{ package: pkg.name }}
                  version={ticketsVersion}
                  emptyTitle={`No tickets for ${pkg.name}`}
                  emptyText="Tickets that include any version of this package appear here."
                  emptyAction={<Button onClick={() => setPanelOpen(true)}>Create tickets</Button>}
                />
              ),
            },
          ]}
        />
      )}
    </EntityPage>
  );
}
